// STEP MINI-CONSOLE-STUDIO-UX-1 · Studio 우측 상단 compact MiniOrder Badge.
//
// 역할: SellerLiveConsole PC header 영역에 올라가는 **아주 작은** 2줄 compact
// 배지. 자사 LIVE /live/{sid}/mini URL 1-tap 복사 · QR 토글(pop) · 주문관리 shortcut.
//
// 중요 가드 — 지시서 "방송 세션이 없을 때는 잘못된 링크를 생성하지 마세요":
//   · streamingSessionId 가 없으면 return null (렌더 자체 skip).
//   · 영상 가림 방지: inline flex 요소만, 고정 overlay 없음, max-width 짧게.
//
// 신규 Backend 0 · 신규 Route 0.
import React, { useEffect, useMemo, useState } from 'react';
import { Link as LinkIcon, Copy, QrCode, ClipboardList } from 'lucide-react';
import QRCode from 'qrcode';
import { toast } from 'sonner';

const MiniOrderStudioBadge = ({ streamingSessionId }) => {
  const [showQR, setShowQR] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');

  const miniUrl = useMemo(() => {
    if (!streamingSessionId) return '';
    try { return `${window.location.origin}/live/${streamingSessionId}/mini`; }
    catch { return ''; }
  }, [streamingSessionId]);

  useEffect(() => {
    if (!miniUrl || !showQR) return;
    let cancelled = false;
    QRCode.toDataURL(miniUrl, { width: 180, margin: 1 })
      .then((u) => { if (!cancelled) setQrDataUrl(u); })
      .catch(() => { if (!cancelled) setQrDataUrl(''); });
    return () => { cancelled = true; };
  }, [miniUrl, showQR]);

  // Session 미생성 상태에서는 UI 자체 미출력 (잘못된 URL 노출 방지).
  if (!streamingSessionId) return null;

  const copy = async () => {
    try { await navigator.clipboard.writeText(miniUrl); toast.success('미니주문 링크를 복사했습니다.'); }
    catch { toast.error('링크 복사 실패'); }
  };

  return (
    <div className="relative inline-flex items-center gap-1"
         data-testid="mini-order-studio-badge">
      <button type="button" onClick={copy}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F7F8FA] dark:bg-white/10 hover:bg-[#EEF0F3] dark:hover:bg-white/20 text-gray-700 dark:text-white text-[11px] font-black transition"
              data-testid="mini-order-studio-badge-copy"
              title={miniUrl}>
        <LinkIcon size={11} className="text-[#FF3B30]" /> 미니주문
        <Copy size={10} />
      </button>
      <button type="button" onClick={() => setShowQR((v) => !v)}
              aria-expanded={showQR}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F7F8FA] dark:bg-white/10 hover:bg-[#EEF0F3] dark:hover:bg-white/20 text-gray-700 dark:text-white text-[11px] font-black transition"
              data-testid="mini-order-studio-badge-qr-toggle">
        <QrCode size={11} /> QR
      </button>
      <a href="/seller/orders" target="_blank" rel="noopener noreferrer"
         className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F7F8FA] dark:bg-white/10 hover:bg-[#EEF0F3] dark:hover:bg-white/20 text-gray-700 dark:text-white text-[11px] font-black transition"
         data-testid="mini-order-studio-badge-orders">
        <ClipboardList size={11} /> 주문
      </a>
      {showQR && (
        <div className="absolute right-0 top-full mt-1.5 z-30 p-2 rounded-lg shadow-xl bg-white dark:bg-[#1a1a1a] border border-black/10 dark:border-white/15"
             data-testid="mini-order-studio-badge-qr-popover">
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="미니주문 QR"
                 className="w-32 h-32 rounded bg-white p-1"
                 data-testid="mini-order-studio-badge-qr-img" />
          ) : (
            <div className="w-32 h-32 flex items-center justify-center text-[10px] text-gray-400">생성 중…</div>
          )}
          <p className="mt-1 text-[9px] text-gray-500 text-center max-w-[128px] break-all">
            {miniUrl.replace(/^https?:\/\//, '')}
          </p>
        </div>
      )}
    </div>
  );
};

export default MiniOrderStudioBadge;
